// Numbers, money and dates in the reader's language: fr-CA gives "1 234,56 $" and
// "2 nov. 2026", en-CA gives "$1,234.56" and "Nov 2, 2026".
// Use useFormat() in components (re-renders when the language changes); the plain
// functions take the locale as their last argument and default to the current one.
import { useTranslation } from "react-i18next";

import { currentLocale } from "./index";
import { localeOf } from "./languages";

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return NaN;
  return typeof value === "number"
    ? value
    : Number(String(value).replace(/,/g, ""));
};

const toDate = (value) => {
  if (value instanceof Date) return value;
  if (value === null || value === undefined || value === "") return null;
  // "2026-11-02" alone is read at noon UTC so it stays the same day in every time zone.
  const s = String(value);
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T12:00:00Z` : s);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatNumber = (value, options = {}, locale = currentLocale()) => {
  const n = toNumber(value);
  if (!Number.isFinite(n))
    return value === null || value === undefined ? "" : String(value);
  return new Intl.NumberFormat(locale, options).format(n);
};

// CAD shows as "$" in English and "$" after the amount in French; other currencies keep
// their code ("US$1.00", "1,00 $ US").
export const formatMoney = (
  value,
  currency = "CAD",
  options = {},
  locale = currentLocale(),
) =>
  formatNumber(
    value,
    {
      style: "currency",
      currency,
      currencyDisplay: currency === "CAD" ? "narrowSymbol" : "symbol",
      ...options,
    },
    locale,
  );

const DATE_STYLES = {
  short: { day: "numeric", month: "short" }, // 2 nov. / Nov 2
  medium: { day: "numeric", month: "short", year: "numeric" }, // 2 nov. 2026 / Nov 2, 2026
  long: { day: "numeric", month: "long", year: "numeric" }, // 2 novembre 2026 / November 2, 2026
  numeric: { year: "numeric", month: "2-digit", day: "2-digit" }, // 2026-11-02 in both
};

export const formatDate = (
  value,
  style = "medium",
  locale = currentLocale(),
) => {
  const d = toDate(value);
  if (!d) return value === null || value === undefined ? "" : String(value);
  const options =
    typeof style === "object"
      ? style
      : DATE_STYLES[style] || DATE_STYLES.medium;
  return d.toLocaleDateString(locale, options);
};

export const formatTime = (value, locale = currentLocale()) => {
  const d = toDate(value);
  if (!d) return value === null || value === undefined ? "" : String(value);
  return d.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
};

export const formatDateTime = (value, locale = currentLocale()) => {
  const d = toDate(value);
  if (!d) return value === null || value === undefined ? "" : String(value);
  return d.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
};

// Country names in the reader's language from the ISO code ("DE" -> "Allemagne"), using
// the browser's own list. English keeps the names our data already has.
const regionNames = {};
export const formatCountry = (
  code,
  fallback = "",
  locale = currentLocale(),
) => {
  const iso = String(code || "").toUpperCase();
  if (!iso || /^en\b/i.test(locale)) return fallback || iso;
  try {
    if (!regionNames[locale]) {
      regionNames[locale] = new Intl.DisplayNames([locale], { type: "region" });
    }
    const name = regionNames[locale].of(iso);
    return name && name !== iso ? name : fallback || iso;
  } catch (e) {
    return fallback || iso; // an older browser, or not a region code
  }
};

// Shown first in every country picker, in this order (as in src/data/countries.js).
const PINNED_COUNTRIES = ["CA", "US"];

/**
 * [{ value: code, label: name }] for a country picker: Canada and the United States
 * first, then the rest sorted in the reader's language.
 */
export const countryOptions = (countries, locale = currentLocale()) => {
  const options = (countries || []).map((c) => ({
    value: c.code,
    label: formatCountry(c.code, c.name, locale),
  }));
  if (/^en\b/i.test(locale)) return options; // our list is already in English order
  const pinned = PINNED_COUNTRIES.map((code) =>
    options.find((o) => o.value === code),
  ).filter(Boolean);
  const rest = options
    .filter((o) => !PINNED_COUNTRIES.includes(o.value))
    .sort((a, b) => a.label.localeCompare(b.label, locale));
  return [...pinned, ...rest];
};

/** The formatters bound to the current language, for components. */
export const useFormat = () => {
  const { i18n } = useTranslation();
  const locale = localeOf(i18n.resolvedLanguage || i18n.language);
  return {
    locale,
    formatNumber: (v, o) => formatNumber(v, o, locale),
    formatMoney: (v, c, o) => formatMoney(v, c, o, locale),
    formatDate: (v, s) => formatDate(v, s, locale),
    formatTime: (v) => formatTime(v, locale),
    formatDateTime: (v) => formatDateTime(v, locale),
    formatCountry: (code, fallback) => formatCountry(code, fallback, locale),
    countryOptions: (countries) => countryOptions(countries, locale),
  };
};
